import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, Modal, Select } from 'antd';
import * as R from 'ramda';

import GenericAutoComplete from '@totem/components/common/autoCompletes/GenericAutoComplete';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import { USER_ROLES } from '@totem/components/userContext/accountUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { RequestUserCreate } from '@totem/components/users/types';
import { AddEditUser } from '@totem/types/user';
import { isNotNull } from '@totem/utilities/common';
import { USERS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { validateEmail } from '@totem/utilities/validation';

const FormItem = Form.Item;

interface FormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface Props {
  visible: boolean;
  loading: boolean;
  onClose: () => void;
}

const initialUser: AddEditUser = {
  role: 0,
  email: '',
  firstName: '',
  lastName: '',
};

const styles = {
  form: {
    width: '100%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
};

export const isValidForm = (user: AddEditUser): FormErrors => {
  const formErrors: FormErrors = {};

  if (R.isEmpty(user.email)) {
    formErrors.email = 'Please enter an email';
  }

  if (user.email && !validateEmail(user.email)) {
    formErrors.email = 'Please enter a valid email';
  }

  if (R.isEmpty(user.firstName)) {
    formErrors.firstName = 'Please enter a first name';
  }

  if (R.isEmpty(user.lastName)) {
    formErrors.lastName = 'Please enter a last name';
  }

  return formErrors;
};

const UserCreateModal = (props: Props) => {
  const { visible, loading, onClose } = props;

  const [user, setUser] = useState<AddEditUser>(initialUser);
  const [errors, setErrors] = useState<FormErrors>({});
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!visible) {
      setErrors({});

      setUser(initialUser);
    }
  }, [visible]);

  const sendUserCreate = useCallback(
    async (request: RequestUserCreate) => {
      if (!sending) {
        setSending(true);

        fetch(`${USERS_ENDPOINT}/create`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(request),
        })
          .then((res) => {
            setSending(false);

            if (!CheckResponseShowError(res)) {
              onClose();
              return null;
            }

            return res.json();
          })
          .then((createdUser) => {
            if (createdUser?.id) {
              navigate(`/dashboard/usermanagement/${createdUser.id}`);
            } else {
              onClose();
            }
          });
      }
    },
    [getApiToken],
  );

  const handleChange = ({
    target: { value, name },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setUser({ ...user, [name]: value });
  };

  const handleSubmit = () => {
    const formErrors = isValidForm(user);

    if (R.isEmpty(formErrors)) {
      const payload: RequestUserCreate = {
        organizationId: userProfile.organization.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        sendNotifications: true,
      };
      sendUserCreate(payload);
    }

    setErrors(formErrors);
  };

  const handleEmailChange = (email: string) => {
    setUser({ ...user, email });
  };

  const roleOptions = USER_ROLES.filter(
    // TODO: Remove @ts-ignore : Token
    // @ts-ignore
    (role) => role.value <= userProfile.organization.role,
  ).map((role) => ({
    value: role.value,
    label: role.label,
  }));

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={<ModalTitle>Add User</ModalTitle>}
      okText="Add"
      confirmLoading={loading}
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={sending}
            onClick={handleSubmit}
            loading={loading || sending}
            type="primary"
          >
            Add
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical" style={styles.form}>
        <FormItem label="Role" colon={false} style={styles.formItem}>
          <Select
            onChange={(role) => setUser({ ...user, role })}
            value={user.role}
            options={roleOptions}
          />
        </FormItem>
        <FormItem
          label="Email"
          colon={false}
          style={styles.formItem}
          validateStatus={R.isNil(errors.email) ? 'success' : 'error'}
          help={R.isNil(errors.email) ? null : errors.email}
        >
          <GenericAutoComplete
            type={'User'}
            label={''}
            limit={15}
            onSelect={(value) => handleEmailChange(value.display)}
            onChange={handleEmailChange}
            allowClear
            allowFreeform={false}
            selectedValue={
              isNotNull(user)
                ? { display: user.email, value: user.id }
                : { display: '', value: '' }
            }
          />
        </FormItem>
        <FormItem
          label="First Name"
          colon={false}
          style={styles.formItem}
          validateStatus={R.isNil(errors.firstName) ? 'success' : 'error'}
          help={R.isNil(errors.firstName) ? null : errors.firstName}
        >
          <Input
            name="firstName"
            value={user.firstName}
            onChange={handleChange}
            type="search"
          />
        </FormItem>
        <FormItem
          label="Last Name"
          colon={false}
          style={styles.formItem}
          validateStatus={R.isNil(errors.lastName) ? 'success' : 'error'}
          help={R.isNil(errors.lastName) ? null : errors.lastName}
        >
          <Input
            name="lastName"
            value={user.lastName}
            onChange={handleChange}
            type="search"
          />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default UserCreateModal;
